import { ProgressBar } from 'components/atoms/index';
import MiniProgressBar from 'components/atoms/ProgressBar/MiniProgressBar';
import clsx from 'clsx';
import catImg from '@/assets/img/cat_test_img.png';
import { useNavigate, useLocation } from 'react-router-dom';

interface LearningItem {
  id: number;
  title: string;
  units: string;
  progress: number;
  startDate?: string;
  endDate: string;
  status?: 'completed' | 'in-progress' | 'waiting';
  thumbnailUrl?: string;
}

interface LearningCardProps {
  title?: string;
  status?: 'completed' | 'waiting' | 'in-progress';
  progress?: number;
  learningItems?: LearningItem[];
  type?: 'learning' | 'homework';
  onCardClick?: (itemId: number) => void;
  onItemClick?: (itemId: number) => void;
}

const learningData: LearningItem[] = [

    {
      id: 2,
      title: "기초 영어 학습",
      units: "Unit 1,2,3",
      progress: 60,
      endDate: "2025.02.15",
      status: 'in-progress'
    },
    {
        id: 3,
        title: "고급 영어 학습",
        units: "Unit 1,2,3",
        progress: 100,
        endDate: "2025.02.15",
        status: 'completed'
      },
      {
        id: 4,
        title: "토익 준비",
        units: "Unit 1,2,3",
        progress: 0,
        endDate: "2025.02.15",
        status: 'waiting'
      }
      
  ];
  
export default function LearningCard({ 
  title = '이름 노출됩니다.',
  status = 'waiting',
  progress = 0,
  learningItems = learningData,
  type,
  onCardClick,
  onItemClick
}: LearningCardProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isHomework = pathname.includes('/homework');
  const cardType = type || (isHomework ? 'homework' : 'learning');
  const normalizedProgress = Number.isFinite(progress)
    ? Math.max(0, Math.min(100, Math.round(progress)))
    : 0;
  // progress가 100%이면 status를 completed로 override
  const actualStatus = normalizedProgress >= 100 ? 'completed' : status;

  // learningItems에도 동일한 로직 적용
  const processedLearningItems = learningItems.map(item => ({
    ...item,
    status: (item.progress >= 100 ? 'completed' : item.status) as 'completed' | 'in-progress' | 'waiting'
  }));

  const handleCardClick = () => {
    // learningItems의 첫 번째 항목 id를 사용
    const itemId = learningItems.length > 0 ? learningItems[0].id : null;
    if (itemId) {
      if (onCardClick) {
        onCardClick(itemId);
      } else {
      navigate(cardType === 'homework' ? `/homework/detail/${itemId}` : `/learning/detail/${itemId}`);
      }
    }
  };
    return (
        <div className='overflow-hidden !rounded-lg card border border-gray-100 lg:border-none cursor-pointer' onClick={handleCardClick}>
            <section className='flex justify-between items-center px-5 py-3 back_gra_ravendar'>
                <p className='text-lg font-bold'>{title}</p>
                <div className="flex items-center gap-2">
                    <span className={clsx(
                        "px-3 py-1 text-xs font-semibold rounded-full",
                        actualStatus === 'completed' && "text-green-600 bg-green-100",
                        actualStatus === 'waiting' && "text-blue-600 bg-blue-100",
                        actualStatus === 'in-progress' && "text-orange-600 bg-orange-100",
                    )}>
                        {actualStatus === 'completed' && (cardType === 'learning' ? '학습완료' : '숙제완료')}
                        {actualStatus === 'waiting' && (cardType === 'learning' ? '학습대기' : '숙제대기')}
                        {actualStatus === 'in-progress' && (cardType === 'learning' ? '학습중' : '숙제중')}
                    </span>
                    <MiniProgressBar value={normalizedProgress} />
                </div>
            </section>
        <ul className='grid grid-cols-1 gap-0 md:grid-cols-2 lg:grid-cols-3'>
            {processedLearningItems.map((item) => (
                <li 
                  key={item.id} 
                  className='flex gap-4 p-4 bg-white border-b border-gray-100 lg:border-none last:border-b-0'
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onItemClick) {
                      onItemClick(item.id);
                    } else {
                      navigate(cardType === 'homework' ? `/homework/detail/${item.id}` : `/learning/detail/${item.id}`);
                    }
                  }}
                >
                    <img 
                        src={item.thumbnailUrl || catImg} 
                        alt={item.title} 
                        className='aspect-[10/14] object-cover max-w-[100px] w-full rounded-lg border border-gray-200' 
                        onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = catImg;
                        }}
                    />
                    <div className='flex flex-col flex-1'>
                        <p className='text-lg font-bold'>{item.title}</p>
                        <p className='text-sm text-gray-500'>{item.units}</p>
                            <div className='py-3'>
                                <ProgressBar 
                                    progress={item.progress} 
                                    showTitle={clsx(
                                        item.status === 'completed' && (cardType === 'learning' ? '학습 완료' : '숙제 완료'),
                                        item.status === 'in-progress' && (cardType === 'learning' ? '학습 중' : '숙제 중'),
                                        item.status === 'waiting' && (cardType === 'learning' ? '학습 대기' : '숙제 대기')
                                    )}
                                    showLabel={true}
                                    titleClassName={clsx(
                                        item.status === 'completed' && '!text-green-600',
                                        item.status === 'in-progress' && '!text-orange-600',
                                        item.status === 'waiting' && '!text-gray-600'
                                    )}
                                    labelClassName={clsx(
                                        item.status === 'completed' && '!text-green-600',
                                        item.status === 'in-progress' && '!text-orange-600',
                                        item.status === 'waiting' && '!text-gray-600'
                                    )}
                                />
                            </div>
                        <p className='text-sm text-gray-500'>
                          {cardType === 'learning' ? '학습기간' : '숙제기간'} : {item.startDate ? `${item.startDate} ~ ` : ''}{item.endDate}
                        </p>
                    </div>
                </li>
            ))}
        </ul>
        </div>
    )
}